import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Boxes, Building2, ChevronDown, LayoutGrid } from "lucide-react";
import {
  cn,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import type { LlmProvider, LlmUsageTenantOption } from "@/features/llm-usage/api/llm-usage.api";
import { llmUsageFeaturesQueryOptions } from "@/lib/queries";
import { translateLlmUsageFeature } from "../lib/llm-usage-feature-labels";
import { brandForProvider, humanizeProvider } from "../lib/model-display";
import { ProviderIcon } from "./ProviderIcon";
import { TenantMark } from "./TenantMark";
import type { LlmUsageFiltersState } from "./llm-usage-filters";

interface LlmUsageScopeFiltersProps {
  filters: LlmUsageFiltersState;
  onFiltersChange: (next: LlmUsageFiltersState) => void;
  /** Scopes the option lists to what actually spent something in the window. */
  windowHours: number;
  tenants: LlmUsageTenantOption[] | undefined;
  isTenantsLoading: boolean;
}

/** Radix Select has no empty-string value, so "all" needs a sentinel. */
const ALL_FEATURES = "__all__";
const ALL_TENANTS = "__all_tenants__";
const ALL_PROVIDERS = "__all_providers__";

/** Both vendors Nexus calls on Vertex, each under its own brand glyph. */
const PROVIDER_OPTIONS: readonly LlmProvider[] = ["GEMINI", "ANTHROPIC"];

/** A notch smaller than the table's mark; the trigger is only 32px tall. */
const TENANT_OPTION_LOGO_SIZE = 18;

/**
 * Two of these three filters are comboboxes rather than selects — their lists
 * run to every feature and every tenant that spent a token — so their triggers
 * borrow the select's shell to keep the three reading as one row.
 */
const FILTER_TRIGGER_CLASS =
  "flex h-8 items-center justify-between gap-2 rounded-sm border border-input-border bg-input-background px-3 py-1 text-xs text-foreground outline-none transition-colors focus:border-input-focus-border";

/** The unscoped row, carrying no branding of its own. */
const ALL_TENANTS_OPTION: LlmUsageTenantOption = {
  tenantId: ALL_TENANTS,
  tenantName: null,
  brandColor: null,
  logoUrl: null,
};

function featureOptionLabel(t: TFunction, feature: string, allLabel: string): string {
  return feature === ALL_FEATURES ? allLabel : translateLlmUsageFeature(t, feature);
}

function tenantOptionLabel(option: LlmUsageTenantOption | null, allLabel: string): string {
  if (!option || option.tenantId === ALL_TENANTS) return allLabel;
  // A tenant whose row is gone still spent the tokens, so the id stands in.
  return option.tenantName ?? option.tenantId;
}

function TenantOptionMark({ option }: { option: LlmUsageTenantOption | null }) {
  if (!option || option.tenantId === ALL_TENANTS) {
    return <Building2 className="size-[18px] shrink-0 text-muted-foreground" aria-hidden />;
  }
  return (
    <TenantMark
      name={option.tenantName}
      logoUrl={option.logoUrl}
      brandColor={option.brandColor}
      size={TENANT_OPTION_LOGO_SIZE}
    />
  );
}

/**
 * The three dimensions the page can be scoped to, widest first.
 *
 * Both option lists are fetched unfiltered on purpose: scoping them to the
 * current selection would leave a dropdown holding only the option already
 * picked, with no way back to the others.
 */
export function LlmUsageScopeFilters({
  filters,
  onFiltersChange,
  windowHours,
  tenants,
  isTenantsLoading,
}: LlmUsageScopeFiltersProps) {
  const { t } = useTranslation();

  const features = useQuery(llmUsageFeaturesQueryOptions(windowHours));
  const allFeaturesLabel = t("admin.llmUsage.filters.allFeatures", "All features");
  const featureOptions = useMemo(() => [ALL_FEATURES, ...(features.data ?? [])], [features.data]);

  const allTenantsLabel = t("admin.llmUsage.filters.allTenants", "All tenants");
  const tenantOptions = useMemo(() => [ALL_TENANTS_OPTION, ...(tenants ?? [])], [tenants]);

  return (
    <>
      <Select
        value={filters.provider ?? ALL_PROVIDERS}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            provider: value === ALL_PROVIDERS ? undefined : (value as LlmProvider),
          })
        }
      >
        <SelectTrigger
          className="h-8 w-[170px] text-xs"
          aria-label={t("admin.llmUsage.filters.providerLabel", "Filter by provider")}
        >
          <SelectValue placeholder={t("admin.llmUsage.filters.allProviders", "All providers")} />
        </SelectTrigger>
        {/* Radix mirrors the chosen item's content into the trigger, so the
            vendor glyph follows the selection up without a second copy. */}
        <SelectContent>
          <SelectItem value={ALL_PROVIDERS}>
            <span className="flex items-center gap-2">
              <Boxes className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              {t("admin.llmUsage.filters.allProviders", "All providers")}
            </span>
          </SelectItem>
          {PROVIDER_OPTIONS.map((provider) => (
            <SelectItem key={provider} value={provider}>
              <span className="flex items-center gap-2">
                <ProviderIcon brand={brandForProvider(provider)} />
                {humanizeProvider(provider)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Typed into for the same reason the tenant filter is: every surface
          that has ever called an LLM lands in this list, and it grows with
          the product. */}
      <SearchableCombobox<string>
        items={featureOptions}
        value={filters.feature ?? ALL_FEATURES}
        onSelect={(feature) =>
          onFiltersChange({
            ...filters,
            feature: feature === ALL_FEATURES ? undefined : feature,
          })
        }
        getKey={(feature) => feature}
        getLabel={(feature) => featureOptionLabel(t, feature, allFeaturesLabel)}
        loading={features.isLoading}
        ariaLabel={t("admin.llmUsage.filters.featureLabel", "Filter by feature")}
        searchPlaceholder={t("admin.llmUsage.filters.searchFeatures", "Search features…")}
        emptyMessage={t("admin.llmUsage.filters.noFeaturesMatch", "No features match your search.")}
        renderItem={(feature) => (
          <span className="min-w-0 flex-1 truncate text-xs">
            {featureOptionLabel(t, feature, allFeaturesLabel)}
          </span>
        )}
        renderTrigger={({ selected, open }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={t("admin.llmUsage.filters.featureLabel", "Filter by feature")}
            className={cn(FILTER_TRIGGER_CLASS, "w-[240px]", open && "border-input-focus-border")}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <LayoutGrid className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate text-left">
                {featureOptionLabel(t, selected ?? ALL_FEATURES, allFeaturesLabel)}
              </span>
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        )}
      />

      {/* A platform with hundreds of tenants makes a plain dropdown a scroll
          hunt, so this one is typed into. The "all" row rides inside the list
          rather than beside it, which keeps one code path for the keyboard,
          the filter, and what the trigger shows. */}
      <SearchableCombobox<LlmUsageTenantOption>
        items={tenantOptions}
        value={filters.tenantId ?? ALL_TENANTS}
        onSelect={(option) =>
          onFiltersChange({
            ...filters,
            tenantId: option.tenantId === ALL_TENANTS ? undefined : option.tenantId,
          })
        }
        getKey={(option) => option.tenantId}
        getLabel={(option) => tenantOptionLabel(option, allTenantsLabel)}
        loading={isTenantsLoading}
        ariaLabel={t("admin.llmUsage.filters.tenantLabel", "Filter by tenant")}
        searchPlaceholder={t("admin.llmUsage.filters.searchTenants", "Search tenants…")}
        emptyMessage={t("admin.llmUsage.filters.noTenantsMatch", "No tenants match your search.")}
        renderItem={(option) => (
          <span className="flex min-w-0 items-center gap-2 text-xs">
            <TenantOptionMark option={option} />
            <span className="truncate">{tenantOptionLabel(option, allTenantsLabel)}</span>
          </span>
        )}
        renderTrigger={({ selected, open }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={t("admin.llmUsage.filters.tenantLabel", "Filter by tenant")}
            className={cn(FILTER_TRIGGER_CLASS, "w-[240px]", open && "border-input-focus-border")}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <TenantOptionMark option={selected} />
              <span className="truncate text-left">
                {tenantOptionLabel(selected, allTenantsLabel)}
              </span>
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        )}
      />
    </>
  );
}
